import React, { useEffect, useMemo, useState } from "react";

type CookieCategoryId = "necessary" | "preferences" | "analytics" | "marketing";

type CookiePreferences = Record<CookieCategoryId, boolean>;

type StoredCookieConsent = {
  version: string;
  preferences: CookiePreferences;
  updatedAt: string;
};

const CONSENT_STORAGE_KEY = "asempera_cookie_consent_v1";
const CONSENT_VERSION = "2026-06-10";

const defaultPreferences: CookiePreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

const cookieCategories: {
  id: CookieCategoryId;
  title: string;
  description: string;
  required?: boolean;
}[] = [
  {
    id: "necessary",
    title: "Necesare",
    description:
      "Asigură funcționarea corectă a site-ului, securitatea și preferințele de bază.",
    required: true,
  },
  {
    id: "preferences",
    title: "Preferințe",
    description:
      "Rețin alegeri precum setările salvate, pentru o experiență mai comodă la revenire.",
  },
  {
    id: "analytics",
    title: "Analiză",
    description:
      "Ne ajută să înțelegem cum este folosit site-ul, ca să îmbunătățim conținutul și performanța.",
  },
  {
    id: "marketing",
    title: "Marketing",
    description:
      "Permit măsurarea campaniilor și afișarea de conținut sau reclame mai relevante.",
  },
];

function readStoredConsent(): StoredCookieConsent | null {
  try {
    const rawConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!rawConsent) return null;

    const parsedConsent = JSON.parse(rawConsent) as StoredCookieConsent;
    if (parsedConsent.version !== CONSENT_VERSION) return null;
    if (!parsedConsent.preferences) return null;

    return parsedConsent;
  } catch {
    return null;
  }
}

function pushGoogleConsent(preferences: CookiePreferences) {
  const gtag = (window as any).gtag;

  if (typeof gtag === "function") {
    gtag("consent", "update", {
      ad_storage: preferences.marketing ? "granted" : "denied",
      ad_user_data: preferences.marketing ? "granted" : "denied",
      ad_personalization: preferences.marketing ? "granted" : "denied",
      analytics_storage: preferences.analytics ? "granted" : "denied",
      functionality_storage: preferences.preferences ? "granted" : "denied",
      personalization_storage: preferences.preferences ? "granted" : "denied",
      security_storage: "granted",
    });
  }

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: "cookie_consent_update",
    cookie_consent: preferences,
  });
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);

  const allOptionalEnabled = useMemo(
    () => cookieCategories.every((category) => preferences[category.id]),
    [preferences],
  );

  useEffect(() => {
    const storedConsent = readStoredConsent();

    if (storedConsent) {
      setPreferences(storedConsent.preferences);
      pushGoogleConsent(storedConsent.preferences);
      return;
    }

    setVisible(true);
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      const storedConsent = readStoredConsent();
      setPreferences(storedConsent?.preferences ?? defaultPreferences);
      setPreferencesOpen(true);
      setVisible(true);
    };

    window.addEventListener(
      "asempera:open-cookie-preferences",
      openPreferences,
    );

    return () => {
      window.removeEventListener(
        "asempera:open-cookie-preferences",
        openPreferences,
      );
    };
  }, []);

  const saveConsent = (nextPreferences: CookiePreferences) => {
    const consent: StoredCookieConsent = {
      version: CONSENT_VERSION,
      preferences: nextPreferences,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    setPreferences(nextPreferences);
    pushGoogleConsent(nextPreferences);
    setVisible(false);
    setPreferencesOpen(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectOptional = () => {
    saveConsent(defaultPreferences);
  };

  const togglePreference = (categoryId: CookieCategoryId) => {
    if (categoryId === "necessary") return;

    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      [categoryId]: !currentPreferences[categoryId],
    }));
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent" role="region" aria-label="Setări cookies">
      {preferencesOpen && (
        <div className="cookie-consent__backdrop">
          <div
            className="cookie-consent__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-consent-title"
          >
            <div className="cookie-consent__modal-header">
              <div>
                <p className="cookie-consent__eyebrow">Confidențialitate</p>
                <h2 id="cookie-consent-title">Setări cookies</h2>
              </div>
              <button
                type="button"
                className="cookie-consent__icon-button"
                onClick={() => setPreferencesOpen(false)}
                aria-label="Închide setările cookies"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <p className="cookie-consent__description">
              Poți alege ce categorii de cookie-uri permiți. Cookie-urile
              necesare rămân active pentru ca site-ul să funcționeze corect.
            </p>

            <div className="cookie-consent__categories">
              {cookieCategories.map((category) => (
                <label className="cookie-consent__category" key={category.id}>
                  <span>
                    <strong>{category.title}</strong>
                    <small>{category.description}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={preferences[category.id]}
                    disabled={category.required}
                    onChange={() => togglePreference(category.id)}
                  />
                </label>
              ))}
            </div>

            <div className="cookie-consent__modal-actions">
              <button
                type="button"
                className="cookie-consent__button cookie-consent__button--secondary"
                onClick={() => saveConsent(preferences)}
              >
                Salvează setările
              </button>
              <button
                type="button"
                className="cookie-consent__button cookie-consent__button--primary"
                onClick={acceptAll}
                disabled={allOptionalEnabled}
              >
                Acceptă toate
              </button>
            </div>
          </div>
        </div>
      )}

      {!preferencesOpen && (
        <div className="cookie-consent__banner">
          <div>
            <p className="cookie-consent__eyebrow">Cookies</p>
            <h2>Folosim cookie-uri</h2>
            <p>
              Utilizăm cookie-uri necesare pentru funcționarea site-ului și, cu
              acordul tău, cookie-uri pentru analiză și marketing.
            </p>
            <a href="/cookies">Politica de cookies</a>
          </div>
          <div className="cookie-consent__actions">
            <button
              type="button"
              className="cookie-consent__button cookie-consent__button--ghost"
              onClick={() => setPreferencesOpen(true)}
            >
              Personalizează
            </button>
            <button
              type="button"
              className="cookie-consent__button cookie-consent__button--secondary"
              onClick={rejectOptional}
            >
              Respinge
            </button>
            <button
              type="button"
              className="cookie-consent__button cookie-consent__button--primary"
              onClick={acceptAll}
            >
              Acceptă
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
